import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { OrgPermission } from '../../../../../backend/src/organisation/organisation-permission';
import { Input } from '../../../components/atoms/input';
import { Button } from '../../../components/atoms/new-button';
import { useUpdateOrganisation } from '@/api/mutations/useUpdateOrganisation';
import { useUpdateOrganisationBillingPeriod } from '@/api/mutations/useUpdateOrganisationBillingPeriod';
import { useUpdateOrganisationIsTrialPackage } from '@/api/mutations/useUpdateOrganisationIsTrialPackage';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { Label } from '@/components/atoms/label';
import { Switch } from '@/components/atoms/switch';
import PageWrapper from '@/components/molecules/PageWrappers/PageWrapper';
import { useGlobalAdminCheck } from '@/hooks/useGlobalAdminCheck';

interface PermissionConfig {
  id: string;
  title: string;
  description: string;
  permission: OrgPermission;
}

const permissionConfigs: PermissionConfig[] = [
  {
    id: 'api-deck-toggle',
    title: 'Apideck Integration',
    description: 'Whether the Apideck integration is enabled for this organisation.',
    permission: OrgPermission.Apideck,
  },
  {
    id: 'anthropic-toggle',
    title: 'Anthropic Models',
    description: 'Whether or not Anthropic models (Claude) through Frankfurt are enabled for this organisation.',
    permission: OrgPermission.AnthropicModels,
  },
  {
    id: 'inconsistency-toggle',
    title: 'Inconsistencies',
    description:
      'Whether or not the Inconsistency feature is enabled in this organisation. This requires Anthropic models to be enabled.',
    permission: OrgPermission.Inconsistencies,
  },
  {
    id: 'gemini-toggle',
    title: 'Gemini Models',
    description: 'Whether or not Gemini models are enabled for this organisation.',
    permission: OrgPermission.GeminiModels,
  },
  {
    id: 'uk-only-toggle',
    title: 'UK Only',
    description: 'Whether the data is restricted to be processed in the UK only.',
    permission: OrgPermission.UkOnly,
  },
  {
    id: 'admin-approval-toggle',
    title: 'Admin Approval',
    description: 'Whether the admin approval feature is enabled for this organisation.',
    permission: OrgPermission.AdminApproval,
  },
];

interface PermissionRowProps {
  config: PermissionConfig;
  isEnabled: boolean;
  onToggle: (permission: OrgPermission) => void;
  disabled?: boolean;
}

const PermissionRow = ({ config, isEnabled, onToggle, disabled }: PermissionRowProps) => (
  <div className="mb-6">
    <div className="flex items-center justify-between">
      <Label htmlFor={config.id} className="text-sm font-medium text-gray-700">
        {config.title}
      </Label>
      <div className="flex items-center gap-2">
        <Switch
          className="bg-gray-100"
          id={config.id}
          disabled={disabled}
          checked={isEnabled}
          onCheckedChange={() => onToggle(config.permission)}
        />
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium ${isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
        >
          {isEnabled ? 'ON' : 'OFF'}
        </span>
      </div>
    </div>
    <p className="mt-1 text-xs text-gray-500">{config.description}</p>
  </div>
);

export const OrganisationEditor = () => {
  const { orgId } = useParams<{ orgId: string }>();

  const { data: organisation } = useGetOrganisation(orgId ?? '');
  const { mutate: updateOrganisation } = useUpdateOrganisation(orgId ?? '');
  const { mutate: updateBillingPeriod, isLoading: isUpdatingBillingPeriod } = useUpdateOrganisationBillingPeriod(
    orgId ?? '',
  );
  const { mutate: updateIsTrialPackage, isLoading: isUpdatingTrialPackage } = useUpdateOrganisationIsTrialPackage(
    orgId ?? '',
  );

  const [maxCredits, setMaxCredits] = useState<number | undefined>();
  const [billingStartDate, setBillingStartDate] = useState<Date | undefined>();
  const [billingEndDate, setBillingEndDate] = useState<Date | undefined>();

  // update field values when organisation updates
  useEffect(() => {
    setMaxCredits(organisation?.maxCredits ?? undefined);
    setBillingStartDate(organisation?.billingStartDate ? new Date(organisation.billingStartDate) : undefined);
    setBillingEndDate(organisation?.billingEndDate ? new Date(organisation?.billingEndDate) : undefined);
  }, [organisation]);

  useGlobalAdminCheck();

  function togglePermission(permission: OrgPermission) {
    if (!organisation) return;

    let updatedPermissions = organisation.permissions.includes(permission)
      ? organisation.permissions.filter((perm) => perm !== permission)
      : [...organisation.permissions, permission];

    // Remove Inconsistency permission if AnthropicModels is disabled
    if (!updatedPermissions.includes(OrgPermission.AnthropicModels)) {
      updatedPermissions = updatedPermissions.filter((perm) => perm !== OrgPermission.Inconsistencies);
    }

    updateOrganisation({ permissions: updatedPermissions });

    toast.success('Organisation permissions updated');
  }

  function handleBillingPeriodSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (maxCredits === undefined || !billingStartDate || !billingEndDate) {
      toast.error(`Billing period fields can't be empty`);
      return;
    }

    updateBillingPeriod({
      maxCredits,
      billingStartDate: billingStartDate,
      billingEndDate: billingEndDate,
    });
  }

  function handleTrialPackageToggle(checked: boolean) {
    updateIsTrialPackage({ isUsingTrialPackage: checked });
  }

  function dateToDatetimeLocal(d: Date): string {
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, -1);
  }

  return (
    <PageWrapper className="bg-gray-50">
      <div className="flex w-full flex-col items-start justify-start px-4 md:px-6 lg:px-8">
        <div className="mb-8 flex w-full flex-col gap-6">
          <div className="w-full rounded-xl bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col">
                <h1 className="text-xl font-semibold text-gray-800">Organisation Settings</h1>
                <p className="text-sm text-gray-600">Manage your organisation's permissions and settings.</p>
              </div>
            </div>
          </div>

          <div className="w-full rounded-xl bg-white p-6 shadow-sm">
            <div className="w-full overflow-x-auto">
              <div className="mb-6">
                <Label htmlFor="org-name" className="mb-2 block text-sm font-medium text-gray-700">
                  Organisation Name
                </Label>
                <div className="flex h-10 w-full items-center rounded-md border border-gray-300 bg-gray-100 px-3 text-gray-700">
                  {organisation?.name}
                </div>
                <p className="mt-1 text-xs text-gray-500">Organisation name cannot be edited</p>
              </div>

              {permissionConfigs.map((config) => (
                <PermissionRow
                  key={config.id}
                  config={config}
                  isEnabled={organisation?.permissions.includes(config.permission) ?? false}
                  onToggle={togglePermission}
                  disabled={
                    config.permission === OrgPermission.Inconsistencies &&
                    !organisation?.permissions.includes(OrgPermission.AnthropicModels)
                  }
                />
              ))}

              <div>
                <p>Billing Period Information</p>
                <div className="my-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-gray-700">Onboarding Package</Label>
                    <div className="flex items-center gap-2">
                      <Switch
                        className="bg-gray-100"
                        id="trial-package-toggle"
                        checked={organisation?.isUsingTrialPackage ?? false}
                        onCheckedChange={handleTrialPackageToggle}
                        disabled={isUpdatingTrialPackage}
                      />
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${organisation?.isUsingTrialPackage ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
                      >
                        {organisation?.isUsingTrialPackage ? 'ON' : 'OFF'}
                      </span>
                    </div>
                  </div>
                  <p className="mt-1 max-w-xl text-xs text-gray-500">
                    Whether this organisation is currently on an onboarding package with increased usage limits. This
                    will display additional information in the usage dashboard (and otherwise has no other effects).
                  </p>
                </div>

                <form onSubmit={handleBillingPeriodSubmit}>
                  <div className="my-2 flex items-center justify-between">
                    <div className="flex gap-2">
                      <div>
                        <Label>Maximum Credits</Label>
                        <Input
                          type="number"
                          className="text-right"
                          value={maxCredits ?? ''}
                          onChange={(e) => setMaxCredits(Number(e.target.value))}
                          disabled={isUpdatingBillingPeriod}
                        />
                      </div>

                      <div>
                        <Label>Billing Period Start Date</Label>
                        <Input
                          type="datetime-local"
                          value={billingStartDate ? dateToDatetimeLocal(billingStartDate) : ''}
                          onChange={(e) => setBillingStartDate(new Date(e.target.value))}
                          disabled={isUpdatingBillingPeriod}
                        />
                      </div>

                      <div>
                        <Label>Billing Period End Date</Label>
                        <Input
                          type="datetime-local"
                          value={billingEndDate ? dateToDatetimeLocal(billingEndDate) : ''}
                          onChange={(e) => setBillingEndDate(new Date(e.target.value))}
                          disabled={isUpdatingBillingPeriod}
                        />
                      </div>
                    </div>

                    <Button variant={'brand'} type="submit" disabled={isUpdatingBillingPeriod}>
                      Update Billing Period
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};
